import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Delete, RotateCcw, X } from 'lucide-react';
import './Calculator.css';

const HISTORY_KEY = 'revstar_calculator_history';
const HISTORY_LIMIT = 20;

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, HISTORY_LIMIT) : [];
  } catch {
    return [];
  }
}

function saveHistory(entries) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, HISTORY_LIMIT)));
  } catch {
    // localStorage full or unavailable — non-fatal
  }
}

function formatDisplay(value) {
  if (value === '' || value === null || value === undefined) return '0';
  const str = String(value);
  if (str === '-') return '-';
  const [intPart, decPart] = str.split('.');
  const negative = intPart.startsWith('-');
  const absInt = negative ? intPart.slice(1) : intPart;
  const grouped = absInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const result = (negative ? '-' : '') + grouped + (decPart !== undefined ? '.' + decPart : '');
  return result;
}

function compute(left, op, right) {
  const a = parseFloat(left);
  const b = parseFloat(right);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  switch (op) {
    case '+': return a + b;
    case '-': return a - b;
    case '×': return a * b;
    case '÷': return b === 0 ? null : a / b;
    default: return null;
  }
}

function trimResult(n) {
  if (n === null || !Number.isFinite(n)) return 'Error';
  // Avoid floating-point fuzz like 0.1 + 0.2 = 0.30000000000000004
  const rounded = Math.round(n * 1e10) / 1e10;
  return String(rounded);
}

/**
 * Shared calculator UI used by both the modal and page shells.
 *
 * Props:
 *   compact (bool)   — render the tighter modal layout. Default false (page mode).
 */
const Calculator = ({ compact = false }) => {
  const [current, setCurrent] = useState('0');
  const [previous, setPrevious] = useState(null);
  const [operator, setOperator] = useState(null);
  const [awaitingNext, setAwaitingNext] = useState(false);
  const [expression, setExpression] = useState('');
  const [history, setHistory] = useState(() => loadHistory());
  const [showHistory, setShowHistory] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    saveHistory(history);
  }, [history]);

  const reset = useCallback(() => {
    setCurrent('0');
    setPrevious(null);
    setOperator(null);
    setAwaitingNext(false);
    setExpression('');
  }, []);

  const inputDigit = useCallback((d) => {
    if (current === 'Error') reset();
    if (awaitingNext) {
      setCurrent(d);
      setAwaitingNext(false);
      return;
    }
    if (current === '0') {
      setCurrent(d);
    } else if (current.replace('-', '').replace('.', '').length < 14) {
      setCurrent(current + d);
    }
  }, [current, awaitingNext, reset]);

  const inputDot = useCallback(() => {
    if (current === 'Error') reset();
    if (awaitingNext) {
      setCurrent('0.');
      setAwaitingNext(false);
      return;
    }
    if (!current.includes('.')) {
      setCurrent(current + '.');
    }
  }, [current, awaitingNext, reset]);

  const toggleSign = useCallback(() => {
    if (current === '0' || current === 'Error') return;
    setCurrent(current.startsWith('-') ? current.slice(1) : '-' + current);
  }, [current]);

  const percent = useCallback(() => {
    if (current === 'Error') return;
    const n = parseFloat(current);
    if (Number.isNaN(n)) return;
    setCurrent(trimResult(n / 100));
  }, [current]);

  const backspace = useCallback(() => {
    if (current === 'Error') { reset(); return; }
    if (awaitingNext) return;
    if (current.length <= 1 || (current.length === 2 && current.startsWith('-'))) {
      setCurrent('0');
    } else {
      setCurrent(current.slice(0, -1));
    }
  }, [current, awaitingNext, reset]);

  const applyOperator = useCallback((op) => {
    if (current === 'Error') return;
    if (previous !== null && operator && !awaitingNext) {
      const result = compute(previous, operator, current);
      const r = trimResult(result);
      setPrevious(r);
      setCurrent(r);
      setExpression(`${formatDisplay(r)} ${op}`);
    } else {
      setPrevious(current);
      setExpression(`${formatDisplay(current)} ${op}`);
    }
    setOperator(op);
    setAwaitingNext(true);
  }, [current, previous, operator, awaitingNext]);

  const equals = useCallback(() => {
    if (previous === null || operator === null) return;
    if (current === 'Error') return;
    const result = compute(previous, operator, current);
    const r = trimResult(result);
    const fullExpression = `${formatDisplay(previous)} ${operator} ${formatDisplay(current)}`;
    setCurrent(r);
    setExpression('');
    setPrevious(null);
    setOperator(null);
    setAwaitingNext(true);
    if (r !== 'Error') {
      setHistory((prev) => [{
        id: Date.now(),
        expression: fullExpression,
        result: r,
        at: new Date().toISOString()
      }, ...prev].slice(0, HISTORY_LIMIT));
    }
  }, [previous, operator, current]);

  const recallEntry = useCallback((entry) => {
    setCurrent(entry.result);
    setPrevious(null);
    setOperator(null);
    setAwaitingNext(true);
    setExpression('');
    setShowHistory(false);
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, []);

  // Keyboard support
  useEffect(() => {
    const onKey = (e) => {
      // Don't hijack typing in other inputs (e.g. when modal is over a form)
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
      if (!rootRef.current) return;

      if (e.key >= '0' && e.key <= '9') { e.preventDefault(); inputDigit(e.key); return; }
      if (e.key === '.') { e.preventDefault(); inputDot(); return; }
      if (e.key === '+') { e.preventDefault(); applyOperator('+'); return; }
      if (e.key === '-') { e.preventDefault(); applyOperator('-'); return; }
      if (e.key === '*' || e.key === 'x' || e.key === 'X') { e.preventDefault(); applyOperator('×'); return; }
      if (e.key === '/') { e.preventDefault(); applyOperator('÷'); return; }
      if (e.key === '%') { e.preventDefault(); percent(); return; }
      if (e.key === 'Enter' || e.key === '=') { e.preventDefault(); equals(); return; }
      if (e.key === 'Backspace') { e.preventDefault(); backspace(); return; }
      if (e.key === 'Escape') { e.preventDefault(); reset(); return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inputDigit, inputDot, applyOperator, percent, equals, backspace, reset]);

  const buttons = useMemo(() => ([
    { label: 'AC', kind: 'fn', onClick: reset, key: 'ac' },
    { label: '+/−', kind: 'fn', onClick: toggleSign, key: 'sign' },
    { label: '%', kind: 'fn', onClick: percent, key: 'pct' },
    { label: '÷', kind: 'op', onClick: () => applyOperator('÷'), active: operator === '÷', key: 'div' },

    { label: '7', kind: 'num', onClick: () => inputDigit('7'), key: '7' },
    { label: '8', kind: 'num', onClick: () => inputDigit('8'), key: '8' },
    { label: '9', kind: 'num', onClick: () => inputDigit('9'), key: '9' },
    { label: '×', kind: 'op', onClick: () => applyOperator('×'), active: operator === '×', key: 'mul' },

    { label: '4', kind: 'num', onClick: () => inputDigit('4'), key: '4' },
    { label: '5', kind: 'num', onClick: () => inputDigit('5'), key: '5' },
    { label: '6', kind: 'num', onClick: () => inputDigit('6'), key: '6' },
    { label: '−', kind: 'op', onClick: () => applyOperator('-'), active: operator === '-', key: 'sub' },

    { label: '1', kind: 'num', onClick: () => inputDigit('1'), key: '1' },
    { label: '2', kind: 'num', onClick: () => inputDigit('2'), key: '2' },
    { label: '3', kind: 'num', onClick: () => inputDigit('3'), key: '3' },
    { label: '+', kind: 'op', onClick: () => applyOperator('+'), active: operator === '+', key: 'add' },

    { label: <Delete size={18} aria-hidden="true" />, kind: 'fn', onClick: backspace, key: 'bksp', aria: 'Backspace' },
    { label: '0', kind: 'num', onClick: () => inputDigit('0'), key: '0' },
    { label: '.', kind: 'num', onClick: inputDot, key: 'dot' },
    { label: '=', kind: 'eq', onClick: equals, key: 'eq' },
  ]), [reset, toggleSign, percent, applyOperator, inputDigit, inputDot, equals, backspace, operator]);

  return (
    <div ref={rootRef} className={`rsc-calc ${compact ? 'rsc-calc--compact' : ''}`} role="region" aria-label="Calculator">
      <div className="rsc-calc__display">
        <div className="rsc-calc__expression" aria-hidden="true">{expression || ' '}</div>
        <div className="rsc-calc__current" aria-live="polite" aria-atomic="true">
          {formatDisplay(current)}
        </div>
      </div>

      <div className="rsc-calc__toolbar">
        <button
          type="button"
          className={`rsc-calc__toolbtn ${showHistory ? 'is-active' : ''}`}
          onClick={() => setShowHistory((s) => !s)}
          aria-pressed={showHistory}
        >
          <RotateCcw size={14} aria-hidden="true" />
          <span>History</span>
          {history.length > 0 && <span className="rsc-calc__count">{history.length}</span>}
        </button>
      </div>

      {showHistory && (
        <div className="rsc-calc__history" role="list">
          {history.length === 0 ? (
            <div className="rsc-calc__empty">No calculations yet.</div>
          ) : (
            <>
              <div className="rsc-calc__history-head">
                <span>Recent</span>
                <button type="button" className="rsc-calc__history-clear" onClick={clearHistory}>
                  <X size={12} aria-hidden="true" /> Clear
                </button>
              </div>
              {history.map((h) => (
                <button
                  type="button"
                  key={h.id}
                  className="rsc-calc__history-item"
                  onClick={() => recallEntry(h)}
                  role="listitem"
                >
                  <span className="rsc-calc__history-expr">{h.expression}</span>
                  <span className="rsc-calc__history-eq">=</span>
                  <span className="rsc-calc__history-result">{formatDisplay(h.result)}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}

      <div className="rsc-calc__pad" role="group" aria-label="Calculator buttons">
        {buttons.map((b) => (
          <button
            key={b.key}
            type="button"
            className={`rsc-calc__btn rsc-calc__btn--${b.kind} ${b.active ? 'is-active' : ''}`}
            onClick={b.onClick}
            aria-label={b.aria || (typeof b.label === 'string' ? b.label : undefined)}
          >
            {b.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Calculator;
